<script setup lang="ts">
import { Download, FileText, FolderPlus } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { isImageMessageAttachment } from '~/lib/messageAttachments';
import type { MessageAttachment } from '~/types/models';

withDefaults(defineProps<{ attachments?: MessageAttachment[]; canAddToProject?: boolean }>(), {
    attachments: () => [],
    canAddToProject: false,
});
defineEmits<{ addToProject: [attachment: MessageAttachment] }>();

function sizeLabel(bytes?: number) {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
</script>

<template>
    <div v-if="attachments.length" class="mt-2 space-y-1.5">
        <div v-for="attachment in attachments" :key="attachment.fileId" class="flex items-center gap-2 rounded-xl bg-background/70 px-2.5 py-2">
            <img
                v-if="isImageMessageAttachment(attachment)"
                :src="attachment.fileURL"
                :alt="attachment.fileName"
                class="size-9 shrink-0 rounded-lg object-cover"
            >
            <FileText v-else class="size-4 shrink-0" />
            <div class="min-w-0 flex-1">
                <p class="truncate text-xs font-medium">{{ attachment.fileName }}</p>
                <p v-if="sizeLabel(attachment.sizeBytes)" class="text-[10px] text-muted-foreground">{{ sizeLabel(attachment.sizeBytes) }}</p>
            </div>
            <Button v-if="canAddToProject" type="button" variant="ghost" size="icon" class="size-7" aria-label="Add to Project files" @click="$emit('addToProject', attachment)">
                <FolderPlus class="size-3.5" />
            </Button>
            <Button as-child variant="ghost" size="icon" class="size-7">
                <a :href="attachment.fileURL" target="_blank" rel="noopener noreferrer" :aria-label="`Download ${attachment.fileName}`">
                    <Download class="size-3.5" />
                </a>
            </Button>
        </div>
    </div>
</template>
